// src/components/forms/FormButton/FormButton.tsx
import { forwardRef } from "react";
import clsx from "clsx";
import type { FormButtonProps } from "./formButton.types";
import {
  getFormButtonContainerClasses,
  getFormButtonClasses,
  getFormButtonIconClasses,
  getFormButtonLoadingClasses,
  getFormButtonLoadingSpinnerClasses,
} from "./formButton.definitions";

const FormButton = forwardRef<HTMLButtonElement, FormButtonProps>(
  (
    {
      children,
      variant = "primary",
      size = "md",
      loading = false,
      disabled = false,
      icon,
      iconPosition = "left",
      fullWidth = false,
      className,
      buttonClassName,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const hasIcon = Boolean(icon);
    const hasChildren = Boolean(children);

    return (
      <div className={getFormButtonContainerClasses(fullWidth, className)}>
        <button
          ref={ref}
          disabled={isDisabled}
          className={clsx(
            getFormButtonClasses(
              variant,
              size,
              loading,
              isDisabled,
              fullWidth,
              hasIcon,
              hasChildren
            ),
            buttonClassName
          )}
          {...props}
        >
          {/* Loading Spinner */}
          {loading && (
            <div className={getFormButtonLoadingClasses(size)}>
              <svg
                className={getFormButtonLoadingSpinnerClasses(size)}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          )}

          {/* Icon Left */}
          {!loading && hasIcon && iconPosition === "left" && (
            <span className={getFormButtonIconClasses("left", hasChildren)}>
              {icon}
            </span>
          )}

          {/* Children/Text */}
          {hasChildren && (
            <span className={loading ? "opacity-0" : ""}>{children}</span>
          )}

          {/* Icon Right */}
          {!loading && hasIcon && iconPosition === "right" && (
            <span className={getFormButtonIconClasses("right", hasChildren)}>
              {icon}
            </span>
          )}
        </button>
      </div>
    );
  }
);

FormButton.displayName = "FormButton";

export default FormButton;
